import type {
  FieldArray,
  FieldArrayMethodProps,
  FieldArrayPath,
  FieldValues,
  Path,
  UseFieldArrayProps,
} from 'react-hook-form';
import { useFieldArray, useFormContext } from 'react-hook-form';

type KeyDepth = 'shallow' | 'deep';

const collectPaths = (obj: unknown, depth: KeyDepth = 'shallow'): string[] => {
  const out = new Set<string>();

  const walk = (val: unknown, base?: string) => {
    if (Array.isArray(val)) {
      if (base) {
        out.add(base);
      } // treat array as the field itself
      return;
    }
    if (val && typeof val === 'object') {
      // shallow: only top-level keys
      for (const k of Object.keys(val)) {
        const next = base ? `${base}.${k}` : k;

        const v = (val as Record<string, unknown>)[k];
        if (
          depth === 'deep' &&
          v &&
          typeof v === 'object' &&
          !Array.isArray(v)
        ) {
          walk(v, next);
        } else {
          out.add(next);
        }
      }
      return;
    }
    if (base) {
      out.add(base);
    }
  };

  walk(obj);
  return Array.from(out);
};

type LatitudeFieldArrayProps<
  T extends FieldValues,
  N extends FieldArrayPath<T>,
  IdKey extends string = 'id',
> = {
  name: N;
  /**
   * 'deep' if you have nested objects
   * 'shallow' if you only have top-level keys
   * @default 'shallow'
   */
  clearDepth?: KeyDepth;
} & Omit<UseFieldArrayProps<T, N, IdKey>, 'name'>;

type SoftAppendOpts = FieldArrayMethodProps & {
  /** also reset dirty at the array root after append (default: true) */
  keepDirty?: boolean;
  keepTouched?: boolean;
  keepError?: boolean;
  resetRoot?: boolean;
};
export const useLatitudeFieldArray = <
  T extends FieldValues,
  N extends FieldArrayPath<T>,
  IdKey extends string = 'id',
>(
  opts: LatitudeFieldArrayProps<T, N, IdKey>,
) => {
  const { name, clearDepth = 'shallow', ...rest } = opts;
  const { clearErrors, resetField, getValues } = useFormContext<T>();

  const methods = useFieldArray<T, N, IdKey>({ name, ...rest });
  const { append, fields } = methods;

  /**
   * * Soft-append: append then clear initial errors for the new element.
   * * - If errorKeys are omitted, they are derived from `row` (shallow by default).
   */
  const softAppend = (
    row: FieldArray<T, N> | FieldArray<T, N>[],
    errorKeys: ReadonlyArray<Path<FieldArray<T, N>>> | undefined = undefined,
    {
      keepDirty = false, // <- prevent dirty from append itself
      keepTouched = false, // <- mark pristine after append
      keepError = true,
      resetRoot = true, // <- re-baseline array root to current value
      ...opts
    }: SoftAppendOpts = {},
  ) => {
    append(row, opts);

    // * defer until RHF registers fields & resolver runs
    setTimeout(() => {
      // * The default RHF behavior is to force validate array items added via append,
      // * which is not desirable in most cases.
      // * We clear the errors for the new item instead.
      const idx = fields.length;
      const keys =
        errorKeys ??
        collectPaths(Array.isArray(row) ? row[0] : row, clearDepth);
      // ? This just clears the error of the appended item, not the entire array
      clearErrors(keys.map((k) => `${name}.${idx}.${k}` as Path<T>));

      if ((!keepDirty || !keepTouched || !keepError) && resetRoot) {
        // * Re-baseline array root to current value to clear dirty.
        // * This allows to have validations on the root array, and also in the rows independently.
        // * and, not trigger forced error states in new items when added (default RHF behavior).
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any
        resetField(name as any, {
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          defaultValue: getValues(name as any),
          keepDirty,
          keepTouched,
          keepError,
        });
      }
    }, 0);
  };

  return { ...methods, softAppend };
};
